import { useState } from "react";
import { Monitor, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { THEME_COLORS } from "@/components/iv/concept/PreviewSite";

const TABS = [["brand", "Edit Brand Name"], ["hero", "Hero Copy"], ["theme", "Color Theme & Tokens"]];
const input = "w-full rounded-xl border border-white/10 bg-[#060913] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/50";

export const Customizer = ({ spec, setSpec, slug, canEdit, mobile, setMobile, onSaved }) => {
  const [tab, setTab] = useState("brand");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/previews/${slug}`, { brand_name: spec.brand_name, hero_headline: spec.hero_headline, theme: spec.theme });
      setSpec(data.site_spec_json); onSaved?.(data); toast.success("Preview saved");
    } catch (e) { toast.error(String(e.response?.data?.detail || "Couldn't save")); } finally { setBusy(false); }
  };
  return (
    <aside className="iv-glass min-w-0 space-y-4 rounded-2xl p-4 lg:sticky lg:top-40 lg:col-span-4 lg:self-start" data-testid="customizer">
      <h2 className="text-lg font-bold text-white">Visual Concept & Live Customizer</h2>
      <div className="grid grid-cols-3 gap-1 rounded-xl bg-white/[0.04] p-1" role="tablist">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`customizer-tab-${k}`}
            className={`rounded-lg px-2 py-1.5 text-[11px] font-semibold ${tab === k ? "bg-[#2563EB] text-white" : "text-slate-400"}`}>{l}</button>
        ))}
      </div>
      {tab === "brand" && <input className={input} maxLength={60} value={spec.brand_name} aria-label="Brand name"
        onChange={(e) => setSpec({ ...spec, brand_name: e.target.value })} data-testid="customizer-brand-input" />}
      {tab === "hero" && <textarea className={`${input} noscrollbar`} rows={3} maxLength={160} value={spec.hero_headline} aria-label="Hero headline"
        onChange={(e) => setSpec({ ...spec, hero_headline: e.target.value })} data-testid="customizer-headline-input" />}
      {tab === "theme" && (
        <div className="flex gap-2">
          {Object.entries(THEME_COLORS).map(([k, c]) => (
            <button key={k} type="button" onClick={() => setSpec({ ...spec, theme: k })} data-testid={`customizer-theme-${k}`}
              className={`flex-1 rounded-xl border px-2 py-2 text-xs font-semibold capitalize text-white ${spec.theme === k ? "border-white" : "border-white/10"}`} style={{ background: `${c}55` }}>{k}</button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => setMobile(false)} aria-pressed={!mobile} data-testid="viewport-desktop"
          className={`inline-flex items-center justify-center gap-1 rounded-full border px-3 py-1.5 text-xs ${!mobile ? "border-cyan-400 text-cyan-300" : "border-white/10 text-slate-400"}`}><Monitor className="h-3.5 w-3.5" /> Desktop</button>
        <button type="button" onClick={() => setMobile(true)} aria-pressed={mobile} data-testid="viewport-mobile"
          className={`inline-flex items-center justify-center gap-1 rounded-full border px-3 py-1.5 text-xs ${mobile ? "border-cyan-400 text-cyan-300" : "border-white/10 text-slate-400"}`}><Smartphone className="h-3.5 w-3.5" /> Mobile 360px</button>
      </div>
      {canEdit ? (
        <button type="button" onClick={save} disabled={busy} className="iv-cta inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white disabled:opacity-60" data-testid="customizer-save-button">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save changes"}
        </button>
      ) : (
        <p className="text-xs text-slate-500" data-testid="customizer-readonly-note">Reference preview — edits apply live here but only the idea's creator can save them.</p>
      )}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Recommended No-Code / AI Stack</h3>
        <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="customizer-stack">
          {spec.recommended_stack.map((s) => <li key={s} className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-slate-300">{s}</li>)}
        </ul>
      </div>
    </aside>
  );
};
